import { uniqueByKey } from '@nx-superprep/utils'

/*
export const getCard = (gameplayType: string) => {
  const cards = getCardsForGameplay(gameplayType)
  return cards[Math.floor(cards.length * Math.random())]
}

export const getAsset = (gameplayType: string) => (getCardsForGameplay(gameplayType).flatMap(c => c.template.choices.flatMap(c => c.resources.flatMap(r => r.asset))))
export const getCardsForGameplay = (gameplayType: string) => (cardSampleJSON.filter(c => c.gameplay.type === gameplayType))
export const getCardForCardID = (id: string) => (cardSampleJSON.filter(c => c.id === id))
*/

/**
 * Interface representing the structure of a card sample.
 */
export interface CardSample {
  id: string
  title?: string
  subTitle?: string
  imageName?: string
  rightAnswerTime?: string
  wrongAnswerTime?: string
}

/**
 * Retrieves card data based on the card ID and domain.
 * @param card The card object containing the ID.
 * @param domain The domain of the card (optional).
 * @returns The card data corresponding to the ID and domain.
 */
export const cardData = (card: { id: string }, domain: string | undefined) => {
  return cardSamples(domain).find((c) => c.id === card.id) ?? {} as CardSample
}

/**
 * Retrieves unique card samples based on the specified subject.
 * @param subject The subject for which to retrieve card samples (default is 'www').
 * @returns Unique card samples corresponding to the subject.
 */
export const cardSamples = (subject = 'languages') => {
  const _sampleMapping: { [key: string]: CardSample[] } = {
    chinese: [
      { id: 'CHIN00101-A10-003-01-EN-FREN006-001' },
      { id: 'CHIN00101-B10-023-01-EN-FREN006-001' },
      { id: 'CHIN00101-A10-005-01-EN-FREN003-001' },
      { id: 'CHIN02210-A10-010-02-EN-FREN006-000' },
      { id: 'CHIN10001-A01-001-02-EN-FREN006-001' },
      { id: 'CHIN10001-A10-011-01-EN-FREN006-001' },
      { id: 'CHIN00101-A10-003-02-EN-FREN006-000' },
      { id: 'CHIN00101-B10-023-02-EN-FREN006-000' },
      { id: 'CHIN00101-A10-005-03-EN-FREN002-001' },
      { id: 'CHIN00101-A10-003-06-EN-FREN006-000' },
      { id: 'CHIN02035-A10-004-01-EN-FREN006-001' },
      { id: 'CHIN02935-A10-002-09-EN-FREN003-000' },
      { id: 'CHIN02410-A10-003-01-EN-FREN006-001' },
      { id: 'CHIN00101-A10-005-03-EN-FREN002-001' },
      { id: 'CHIN00101-B10-023-04-EN-FREN006-001' },
      { id: 'CHIN02935-A10-002-11-EN-FREN006-000' },
      { id: 'CHIN02210-A10-002-01-EN-FREN006-001' },
      { id: 'CHIN02935-A10-002-12-EN-FREN002-000' },
    ],
    french: [
      { id: 'FREN19-T02-001-02-EN-FREN006-000' },
      { id: 'FREN25-T08-003-03-EN-FREN002-000' },
      { id: 'FREN60-T04-017-06-EN-FREN003-000' },
      { id: 'FREN05008-T09-002-04-EN-FREN003-000' },
      { id: 'FREN07104-T04-001-03-EN-FREN003-000' },
      { id: 'FREN03708-T07-003-09-EN-FREN003-000' },
      { id: 'FREN02604-T01-001-05-EN-FREN006-000' },
      { id: 'FREN07305-T12-001-06-EN-FREN003-000' },
      { id: 'FREN08306-T06-001-03-EN-FREN003-000' },
    ],
    biology: [
      { id: 'BIOL1-T04-003-01-EN-BIOL001-000' },
      { id: 'BIOL1-T08-011-02-EN-BIOL001-000' },
      { id: 'BIOL3-T02-001-04-EN-BIOL001-000' },
      { id: 'BIOL3-T08-006-01-EN-BIOL001-000' },
      { id: 'BIOL7-T04-001-04-EN-BIOL001-000' },
      { id: 'BIOL7-T10-008-01-EN-BIOL001-000' },
      { id: 'BIOL13-T01-008-01-EN-BIOL001-000' },
      { id: 'BIOL13-T02-002-02-EN-BIOL001-000' },
      { id: 'BIOL17-T03-001-03-EN-BIOL001-000' },
      { id: 'BIOL17-T11-001-01-EN-BIOL001-000' },
    ],
    apbiology: [
      { id: 'APBI4-T04-002-02-EN-GAME001-000' },
      { id: 'APBI4-T8-002-02-EN-GAME001-000' },
      { id: 'APBI15-T02-002-01-EN-GAME001-000' },
      { id: 'APBI15-T04-008-01-EN-GAME001-000' },
      { id: 'APBI9-T01-003-01-EN-GAME001-000' },
      { id: 'APBI9-T03-018-03-EN-GAME001-000' },
      { id: 'APBI14-T01-001-01-EN-GAME001-000' },
      { id: 'APBI14-T03-006-01-EN-GAME001-000' },
      { id: 'APBI16-T03-003-01-EN-GAME001-000' },
      { id: 'APBI16-T06-006-01-EN-GAME001-000' },
    ],
    medicalstudent: [
      { id: 'BIOL00603-T03-001-03-EN-BIOL1-000' },
      { id: 'BIOL00603-T31-002-02-EN-BIOL1-000' },
      { id: 'BIOL01203-T02-001-07-EN-BIOL1-000' },
      { id: 'BIOL01203-T08-007-02-EN-BIOL1-000' },
      { id: 'BIOL02303-T02-003-03-EN-BIOL1-000' },
      { id: 'BIOL02303-T29-004-02-EN-BIOL1-000' },
      { id: 'BIOL02703-T11-002-02-EN-BIOL1-000' },
      { id: 'BIOL02703-T12-017-01-EN-BIOL1-000' },
      { id: 'BIOL03303-T06-001-01-EN-BIOL1-000' },
      { id: 'BIOL03303-T09-009-01-EN-BIOL1-000' },
    ],
    chemistry: [
      { id: 'CHEM1-T01-016-01-EN-CHEM001-000' },
      { id: 'CHEM1-T01-016-03-EN-CHEM001-000' },
      { id: 'CHEM4-T06-216-01-EN-CHEM004-000' },
      { id: 'CHEM9-T01-059-01-EN-CHEM027-000' },
    ],
    asl: [
      { id: 'ASLG00301-A10-001-01-EN-FREN002-000' },
      { id: 'ASLG00301-A10-001-08-EN-FREN002-000' },
      { id: 'ASLG00301-A10-002-01-EN-FREN002-000' },
      { id: 'ASLG00301-A10-003-01-EN-FREN002-000' },
      { id: 'ASLG00301-A10-004-01-EN-FREN002-000' },
      { id: 'ASLG00301-A10-005-01-EN-FREN002-000' },
      { id: 'ASLG00301-A10-006-01-EN-FREN002-000' },
    ],
    react: [
      { id: 'FREN19-T01-001-03-EN-FREN002-001' },
      { id: 'FREN19-T02-001-03-EN-FREN002-000' },
      { id: 'FREN28-T04-002-03-EN-FREN002-000' },
      { id: 'CHIN00101-A10-003-01-EN-FREN006-001' },
      { id: 'CHIN00101-B10-023-01-EN-FREN006-001' },
      { id: 'GEOG15-T01-001-02-EN-GAME001-000' },
      { id: 'GEOG15-T01-001-03-EN-GAME001-000' },
      { id: 'GEOG1-T01-001-01-EN-GEOG001-000' },
      { id: 'GEOG2-T01-001-01-EN-GEOG004-000' },
      { id: 'GEOG2-T01-002-01-EN-GEOG004-000' },
      { id: 'GEOG3-T02-001-01-EN-GEOG004-000' },
      { id: 'BIOL1-T02-001-01-EN-BIOL001-000' },
    ],
    alphabet: [
      // chinese
      { id: 'TEST002-T08-001-02-EN-GAME001-000' },
      // french
      { id: 'FRENTEST002-T06-001-04-EN-GAME001-000' },
      { id: 'FREN24-T02-002-04-EN-GAME001-000' },
      // french not working
      { id: 'FREN08906-T09-005-07-EN-GAME001-000' },
      //spanish
      { id: 'SPAN022006-T08-044-12-EN-GAME001-000' },
      { id: 'SPAN022006-T08-043-12-EN-GAME001-000' },
      { id: 'SPAN022006-T08-042-12-EN-GAME001-000' },
      { id: 'SPAN022006-T08-041-12-EN-GAME001-000' },
      { id: 'SPAN022006-T08-040-12-EN-GAME001-000' },
      { id: 'SPAN022006-T08-039-12-EN-GAME001-000' },
      { id: 'SPAN019001-T01-001-12-EN-GAME001-000' },
      { id: 'SPAN019001-T02-002-12-EN-GAME001-000' },
      // alphabet 'gameplay'
      { id: 'FRTEST59-T17-004-03-EN-GAME001-000' },
      { id: 'FRTEST59-T17-005-03-EN-GAME001-000' },
      { id: 'TEST002-T10-001-01-EN-GAME001-000' },
    ],
    gameplays: [
      { id: 'CHIN00101-A10-003-01-EN-FREN006-001' },
      { id: 'CHIN00101-B10-023-01-EN-FREN006-001' },
      { id: 'CHIN00101-A10-005-01-EN-FREN003-001' },
      { id: 'CHIN02210-A10-010-02-EN-FREN006-000' },
      { id: 'CHIN10001-A01-001-02-EN-FREN006-001' },
      { id: 'CHIN02935-A10-002-09-EN-FREN003-000' },
      { id: 'CHIN02935-A10-002-11-EN-FREN006-000' },
      { id: 'CHIN02935-A10-002-12-EN-FREN002-000' },

      { id: 'FREN60-T04-017-06-EN-FREN003-000' },
      { id: 'FREN05008-T09-002-04-EN-FREN003-000' },
      { id: 'FREN07104-T04-001-03-EN-FREN003-000' },
      { id: 'FREN03708-T07-003-09-EN-FREN003-000' },

      { id: 'ASLG00301-A10-001-01-EN-FREN002-000' },
      { id: 'ASLG04601-A10-001-01-EN-GAME001-000' },
      { id: 'ASLG04601-A10-001-08-EN-GAME001-000' },

      { id: 'APBI1-T02-007-01-EN-GAME001-000' },
      { id: 'APBI1-T06-001-01-EN-GAME001-000' },
      { id: 'APBI2-T02-001-01-EN-GAME001-000' },
      { id: 'APBI4-T03-001-03-EN-GAME001-000' },
      { id: 'APBI5-T06-005-01-EN-GAME001-000' },

      { id: 'BIOL1-T10-004-01-EN-BIOL001-000' },
      { id: 'BIOL1-T11-002-01-EN-BIOL001-000' },

      { id: 'CHEM1-T01-016-01-EN-CHEM001-000' },
      { id: 'CHEM1-T01-016-03-EN-CHEM001-000' },
      { id: 'CHEM3-T02-172-03-EN-CHEM010-000' },
      { id: 'CHEM4-T06-216-01-EN-CHEM004-000' },
      { id: 'CHEM5-T01-002-01-EN-CHEM031-000' },
      { id: 'CHEM5-T01-004-01-EN-CHEM031-000' },
      { id: 'CHEM5-T06-038-01-EN-CHEM019-000' },
      { id: 'CHEM5-T02-001-01-EN-CHEM021-000' },
      { id: 'CHEM9-T01-059-01-EN-CHEM027-000' },

      { id: 'SPAN9-T08-012-01-EN-FREN005-000' },
      { id: 'SPAN9-T07-008-02-EN-FREN005-000' },
      { id: 'SPAN9-T08-011-02-EN-FREN005-000' },
      { id: 'SPAN9-T26-002-01-EN-FREN005-000' },
      { id: 'SPAN113002-T08-016-06-EN-GAME001-000' },

      { id: 'GEOG26-T02-001-02-EN-GAME001-000' },
      { id: 'GEOG26-T02-001-03-EN-GAME001-000' },

      { id: 'CHEM5-T02-008-01-EN-CHEM021-000' },
      { id: 'CHEM5-T03-002-01-EN-CHEM021-000' },
      { id: 'CHEM6-T01-001-04-EN-CHEM030-000' },
      { id: 'CHEM6-T01-002-02-EN-CHEM028-000' },
      { id: 'CHEM6-T01-003-04-EN-CHEM030-000' },
      { id: 'CHEM6-T01-011-01-EN-CHEM028-000' },
      { id: 'CHEM6-T01-080-04-EN-CHEM030-000' },
      { id: 'CHEM7-T01-009-04-EN-CHEM030-000' },
      { id: 'CHEM7-T01-010-03-EN-CHEM030-000' },
      { id: 'CHEM10-T01-001-02-EN-CHEM029-000' },
      { id: 'CHEM10-T01-004-02-EN-CHEM029-000' },

      { id: 'FREN037001-T02-002-02-EN-GAME001-001' },
      { id: 'TEST002-T08-001-01-EN-GAME001-000' },
      { id: 'TEST002-T08-001-02-EN-GAME001-000' },
      { id: 'TEST002-T08-001-03-EN-GAME001-000' },
      { id: 'TEST002-T08-001-04-EN-GAME001-000' },
      { id: 'TEST002-T08-001-05-EN-GAME001-000' },
      { id: 'TEST001-T03-001-02-EN-GAME001-000' },
      { id: 'TEST002-T10-001-01-EN-GAME001-000' },
      { id: 'TEST002-T05-001-04-EN-GAME001-000' }
    ],
    languages: [
      { id: 'FREN20-T01-004-03-EN-FREN002-001' },
      { id: 'FREN20-T01-002-03-EN-FREN002-000' },
      { id: 'FREN20-T02-001-05-EN-FREN006-001' },
      { id: 'FREN24-T05-003-05-EN-FREN006-001' },
      { id: 'FREN03405-T05-001-02-EN-FREN006-000' },
      { id: 'FREN02105-T02-001-06-EN-FREN002-001' },
      { id: 'CHIN00101-A10-003-01-EN-FREN006-001' },
      { id: 'CHIN00101-B10-023-01-EN-FREN006-001' },
      { id: 'CHIN00101-A10-005-01-EN-FREN003-001' },
      { id: 'CHIN02210-A10-010-02-EN-FREN006-000' },
      { id: 'CHIN10001-A01-001-02-EN-FREN006-001' },
    ]
  }

  const subjectKey = subject.toLowerCase().replace(/^pop/g,'')
  return uniqueByKey<CardSample>(_sampleMapping[subjectKey] || [], 'id')
}
